import { Typography } from "@/components/ui";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/shadcn/Accordion/Accordion";
import { cn } from "@/lib/utils/cn";
import { Plus } from "lucide-react";
import type { DragEvent, ReactNode } from "react";
import {
  FIELD_CONFIG,
  SIGNER_COLORS,
  type FieldType,
  type SignerSlot,
} from "./SignatureRequestPdfLayoutEditor.constants";

type SignatureRequestPdfLayoutEditorSidebarProps = {
  activeSignerId: string;
  onBeginPaletteDrag: (
    event: DragEvent<HTMLButtonElement>,
    type: FieldType,
  ) => void;
  signers: SignerSlot[];
  signerFields?: ReactNode;
  fieldPlacementDisabled?: boolean;
};

export function SignatureRequestPdfLayoutEditorSidebar({
  activeSignerId,
  onBeginPaletteDrag,
  signers,
  signerFields,
  fieldPlacementDisabled = false,
}: SignatureRequestPdfLayoutEditorSidebarProps) {
  const activeSignerIndex = signers.findIndex(
    (signer) => signer.id === activeSignerId,
  );
  const activeSignerColor =
    SIGNER_COLORS[
      (signers[activeSignerIndex]?.colorIndex ??
        Math.max(0, activeSignerIndex)) % SIGNER_COLORS.length
    ];
  const activeSigner = signers.find((signer) => signer.id === activeSignerId);

  return (
    <aside className="border-border bg-card min-h-0 overflow-y-auto border-l">
      <div className="border-border border-b">
        <div className="px-3 py-3">
          <Typography className="text-sm font-semibold">
            Signer fields
          </Typography>
        </div>
        <div className="space-y-1.5 px-3 pb-3">
          {activeSigner ? (
            <div
              className="flex items-center gap-2.5 rounded-lg border px-3 py-2.5"
              style={{
                borderColor: activeSignerColor.selectedBorder,
                backgroundColor: activeSignerColor.selectedBg,
              }}
            >
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: activeSignerColor.border }}
              />
              <div className="min-w-0">
                <div className="text-foreground truncate text-sm font-semibold">
                  {activeSigner.displayName || activeSigner.slotName}
                </div>
                {activeSigner.displayName ? (
                  <div className="text-muted-foreground truncate text-xs">
                    {activeSigner.slotName}
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <Typography className="text-muted-foreground text-xs">
              Select or add a signer to manage fields.
            </Typography>
          )}

          {fieldPlacementDisabled || !activeSigner ? (
            <div className="border-border text-muted-foreground mt-3 rounded-lg border border-dashed p-3 text-xs">
              Add a signer to assign and place fields.
            </div>
          ) : (
            signerFields
          )}
        </div>
      </div>

      <Accordion type="multiple" defaultValue={["types"]} className="w-full">
        <AccordionItem value="types">
          <AccordionTrigger className="px-3 py-3 hover:no-underline">
            <span className="flex items-center gap-2">
              <Plus className="text-muted-foreground h-4 w-4" />
              <Typography className="text-sm font-semibold">
                Add new field
              </Typography>
            </span>
          </AccordionTrigger>
          <AccordionContent className="px-3 pb-3">
            <div
              className={cn(
                "space-y-1.5",
                fieldPlacementDisabled && "opacity-50",
              )}
              aria-disabled={fieldPlacementDisabled}
            >
              <Typography className="text-muted-foreground mb-2 text-xs">
                {fieldPlacementDisabled
                  ? "Add a signer to enable field placement."
                  : "Drag a field onto the document to place it."}
              </Typography>
              {(Object.keys(FIELD_CONFIG) as FieldType[]).map((type) => {
                const Icon = FIELD_CONFIG[type].icon;

                return (
                  <button
                    key={type}
                    type="button"
                    draggable={!fieldPlacementDisabled}
                    disabled={fieldPlacementDisabled}
                    onDragStart={(event) => {
                      onBeginPaletteDrag(event, type);
                    }}
                    className="border-border bg-card hover:bg-accent hover:text-accent-foreground flex w-full items-center gap-2.5 rounded-lg border px-3 py-2 text-sm font-medium shadow-sm transition-colors"
                  >
                    <Icon
                      className="h-4 w-4 shrink-0"
                      style={{ color: activeSignerColor.border }}
                    />
                    {FIELD_CONFIG[type].label}
                  </button>
                );
              })}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </aside>
  );
}
